import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, subDays, startOfDay } from "date-fns";
import { nb } from "date-fns/locale";
import { buildCategoryColorMap, CATEGORY_ORDER } from "./categoryColors";
import type { WeeklyProgressVM, DayRow, CategoryTotal, Insight } from "./types";

interface UseWeeklyProgressDataProps {
  childId: string;
  familyId: string;
  childCreatedAt: string;
}

export function useWeeklyProgressData({
  childId,
  familyId,
  childCreatedAt,
}: UseWeeklyProgressDataProps) {
  const today = useMemo(() => startOfDay(new Date()), []);
  const periodStart = useMemo(() => subDays(today, 14), [today]);
  const trendStart = useMemo(() => subDays(today, 21), [today]);
  const todayStr = useMemo(() => format(today, "yyyy-MM-dd"), [today]);
  const childStartDate = useMemo(
    () => format(subDays(new Date(childCreatedAt), 1), "yyyy-MM-dd"),
    [childCreatedAt]
  );
  const childMaStartDate = useMemo(
    () => format(new Date(childCreatedAt), "yyyy-MM-dd"),
    [childCreatedAt]
  );

  const { data: taskItems, isLoading: tasksLoading } = useQuery({
    queryKey: ["report-tasks", childId, today.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_report_tasks", {
        _child_id: childId,
        _start_date: format(periodStart, "yyyy-MM-dd"),
        _end_date: format(today, "yyyy-MM-dd"),
      });
      if (error) throw error;
      return data;
    },
  });

  const { data: pointTxns, isLoading: pointsLoading } = useQuery({
    queryKey: ["report-points", childId, today.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("point_transactions")
        .select("amount, created_at")
        .eq("child_id", childId)
        .gte("created_at", trendStart.toISOString())
        .lte(
          "created_at",
          new Date(today.getTime() + 86400000).toISOString()
        );
      if (error) throw error;
      return data;
    },
  });

  const vm = useMemo<WeeklyProgressVM | null>(() => {
    if (!taskItems || !pointTxns) return null;

    // 1. Count categories by total done (desc) for ordering
    const counts: Record<string, number> = {};
    for (const t of taskItems) {
      if (t.status === "done") {
        const cat = t.category ?? "Annet";
        counts[cat] = (counts[cat] ?? 0) + 1;
      }
    }
    // Use canonical fixed order, filtered to categories with data
    const categoryOrder = CATEGORY_ORDER.filter((c) => counts[c] > 0) as string[];
    // Add any unknown categories at the end (sorted by count desc)
    const unknown = Object.keys(counts)
      .filter((c) => !(CATEGORY_ORDER as readonly string[]).includes(c))
      .sort((a, b) => counts[b] - counts[a]);
    categoryOrder.push(...unknown);
    const categoryTotals: CategoryTotal[] = categoryOrder.map((c) => ({
      category: c,
      total: counts[c],
    }));
    const categoryColors = buildCategoryColorMap(categoryOrder);

    // 2. Build 22 days of daily points for MA
    const dailyPoints: number[] = [];
    for (let i = 0; i < 22; i++) {
      const d = subDays(today, 21 - i);
      const dateStr = format(d, "yyyy-MM-dd");
      if (dateStr < childStartDate) {
        dailyPoints.push(0);
        continue;
      }
      const dayStart = d;
      const dayEnd = new Date(d.getTime() + 86400000);
      dailyPoints.push(
        pointTxns
          .filter((p) => {
            const t = new Date(p.created_at);
            return t >= dayStart && t < dayEnd;
          })
          .reduce((s, p) => s + p.amount, 0)
      );
    }

    // 3. Build chart rows (15 days)
    const days: DayRow[] = [];
    for (let i = 0; i < 15; i++) {
      const d = subDays(today, 14 - i);
      const dateStr = format(d, "yyyy-MM-dd");
      if (dateStr < childStartDate) continue;

      const isToday = dateStr === todayStr;
      const label = isToday ? "i dag" : format(d, "d.", { locale: nb });

      const row: any = { date: dateStr, label, isToday };
      let dayTotal = 0;
      for (const cat of categoryOrder) {
        const cnt = taskItems.filter(
          (t) =>
            t.date === dateStr &&
            t.status === "done" &&
            (t.category ?? "Annet") === cat
        ).length;
        row[cat] = cnt;
        dayTotal += cnt;
      }

      // Points for day
      const dayStart2 = d;
      const dayEnd2 = new Date(d.getTime() + 86400000);
      row._points = pointTxns
        .filter((p) => {
          const t = new Date(p.created_at);
          return t >= dayStart2 && t < dayEnd2;
        })
        .reduce((s, p) => s + p.amount, 0);

      // 7-day MA
      const dpIdx = 7 + i;
      const window = dailyPoints.slice(Math.max(0, dpIdx - 6), dpIdx + 1);
      const activeDays = window.filter((_, wi) => {
        const windowDayIdx = Math.max(0, dpIdx - 6) + wi;
        const windowDate = format(
          subDays(today, 21 - windowDayIdx),
          "yyyy-MM-dd"
        );
        return windowDate >= childMaStartDate;
      });
      row._pointsMA =
        activeDays.length > 0
          ? Math.round(
              activeDays.reduce((s, v) => s + v, 0) / activeDays.length
            )
          : 0;
      row._totalTasks = dayTotal;
      days.push(row as DayRow);
    }

    // 4. Heatmap matrix [catIdx][dayIdx]
    const heatmapMatrix = categoryOrder.map((cat) =>
      days.map((d) => (d[cat] as number) ?? 0)
    );

    // 5. Insights
    const insights = buildInsights(days, categoryOrder, counts);

    const totalPoints = pointTxns.reduce((s, p) => s + p.amount, 0);
    const totalTasks = taskItems.filter((t) => t.status === "done").length;

    return {
      days,
      categoryTotals,
      categoryOrder,
      categoryColors,
      heatmapMatrix,
      insights,
      totalPoints,
      totalTasks,
    };
  }, [
    taskItems,
    pointTxns,
    today,
    todayStr,
    childStartDate,
    childMaStartDate,
  ]);

  return { vm, isLoading: tasksLoading || pointsLoading };
}

function buildInsights(
  days: DayRow[],
  categoryOrder: string[],
  catCounts: Record<string, number>
): Insight[] {
  const insights: Insight[] = [];
  const totalDone = Object.values(catCounts).reduce((s, v) => s + v, 0);

  if (totalDone === 0) {
    return [{ key: "empty", lucideIcon: "info", value: "—", label: "Ingen data", detail: "Ingen oppgaver registrert i perioden" }];
  }

  // 1. Top category
  if (categoryOrder.length > 0) {
    const top = [...categoryOrder].sort((a, b) => (catCounts[b] ?? 0) - (catCounts[a] ?? 0))[0];
    const topCount = catCounts[top];
    insights.push({
      key: "top",
      lucideIcon: "trophy",
      value: top,
      label: "Toppkategori",
      detail: `${topCount} oppgaver fullført i ${top}`,
    });
  }

  // 2. Coverage
  const totalCategories = categoryOrder.length;
  if (totalCategories > 0) {
    insights.push({
      key: "coverage",
      lucideIcon: "layout-grid",
      value: `${totalCategories}`,
      label: totalCategories === 1 ? "kategori" : "kategorier",
      detail: `Brukt ${totalCategories} av 9 kategorier`,
    });
  }

  // 3. Focus area – least used category (or unused)
  const ALL_CATS = [
    "Akademisk mestring", "Fysisk helse", "Egenomsorg", "Kreativ utfoldelse",
    "Sosial kompetanse", "Ansvar hjemme", "Selvstendighet", "Personlig utvikling",
  ];
  const unused = ALL_CATS.filter((c) => !catCounts[c]);
  if (unused.length > 0) {
    insights.push({
      key: "focus",
      lucideIcon: "target",
      value: unused[0],
      label: "Fokusområde",
      detail: `${unused[0]} har ingen oppgaver – prøv å legge til noen!`,
    });
  } else {
    // All used – suggest the least used
    const sorted = [...categoryOrder].sort((a, b) => (catCounts[a] ?? 0) - (catCounts[b] ?? 0));
    const least = sorted[0];
    if (least) {
      insights.push({
        key: "focus",
        lucideIcon: "target",
        value: least,
        label: "Fokusområde",
        detail: `${least} har bare ${catCounts[least]} oppgaver – rom for mer!`,
      });
    }
  }

  // 4. Trend
  if (days.length >= 14) {
    const prev7 = days.slice(0, 7).reduce((s, d) => s + (d._totalTasks as number), 0);
    const last7 = days.slice(7, 14).reduce((s, d) => s + (d._totalTasks as number), 0);
    const diff = last7 - prev7;
    if (diff !== 0) {
      insights.push({
        key: "trend",
        lucideIcon: diff > 0 ? "trending-up" : "trending-down",
        value: `${diff > 0 ? "+" : ""}${diff}`,
        label: "vs forrige uke",
        detail: `${Math.abs(diff)} ${diff > 0 ? "flere" : "færre"} oppgaver siste 7 dager sammenlignet med forrige 7`,
      });
    }
  }

  return insights.slice(0, 4);
}
